/**
 * The pediatrician report (`/report`): each baby's last seven full days on
 * sheets of their own, from the overview's two weeks of entries. On screen it
 * previews the paper; Print sends it, or saves a PDF from the same dialog.
 * Read-only, and only what was logged: it reads no meaning into the numbers.
 */
import { trackedOnly } from "@babia/checks";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, Printer } from "lucide-react";
import { useMemo } from "react";
import { Glyph } from "../components/layout.tsx";
import { BabyReport } from "../components/report/baby-report.tsx";
import { Button } from "../components/ui/button.tsx";
import { track } from "../lib/analytics.ts";
import { percentileWords, useGrowth } from "../lib/growth.ts";
import { mergeHistory } from "../lib/overview.ts";
import {
  useBabies,
  useBottleUnit,
  useEntries,
  useEntryHistory,
  useTracking,
  useWeightUnit,
} from "../lib/queries.ts";
import { pediatricReport, REPORT_DAYS, reportContents, reportPeriod } from "../lib/report.ts";
import { clock } from "../lib/time.ts";
import { useNow } from "../lib/use-now.ts";
import { cn } from "../lib/utils.ts";

export function Report() {
  const babies = useBabies();
  const live = useEntries();
  const history = useEntryHistory();
  const tracking = useTracking();
  const unit = useBottleUnit();
  const weightUnit = useWeightUnit();
  const now = useNow(60_000);
  const list = useMemo(() => babies.data ?? [], [babies.data]);

  const entries = useMemo(
    () => mergeHistory(live.data, history.data, now),
    [live.data, history.data, now],
  );
  // The live list alone covers two days; never report a week from it. Wait
  // for the household too: until it says what it tracks, the preview (and
  // the browser's own print) could show a kind the family turned off.
  const loaded = history.data !== undefined && tracking.ready;
  // Opened over a cached fortnight: hold Print until the refresh on open lands.
  const refreshing = history.isFetching && !history.isFetchedAfterMount;
  // Paper counts only what the family tracks: a day with nothing but a sleep
  // logged reads "Nothing logged" once sleep is off, and isn't averaged.
  const shown = useMemo(
    () => trackedOnly(entries, tracking.untracked),
    [entries, tracking.untracked],
  );
  const period = useMemo(() => reportPeriod(shown, now), [shown, now]);
  const reports = useMemo(
    () => list.map((b) => ({ baby: b, report: pediatricReport(shown, b.id, now, period) })),
    [shown, list, now, period],
  );
  // A weight on paper reads with its percentile: wait for the WHO table when
  // the report shows a weight with a number, unless the table can't load
  // (then the weight prints alone).
  const weighed = reports.some(({ report }) => report.notes.some((n) => n.weightGrams !== null));
  const { growth, failed } = useGrowth(weighed);
  const ready = loaded && (!weighed || growth !== null || failed);

  return (
    <div className="mx-auto w-full max-w-[800px]">
      <div className="print:hidden">
        <div className="flex items-center gap-2">
          <Link
            to="/overview"
            className="ghost-btn -ml-2 inline-flex h-11 items-center gap-1.5 px-2 text-[15px]"
          >
            <ArrowLeft className="size-5" />
            Overview
          </Link>
          <Button
            variant="primary"
            className="ml-auto min-h-[44px] text-[16px]"
            onClick={() => {
              track("report print opened", { babies: list.length });
              window.print();
            }}
            disabled={!ready || refreshing}
            aria-busy={refreshing}
          >
            <Printer className="size-5" />
            Print
          </Button>
        </div>
        <h1 className="heading mt-3 text-[26px]">For the pediatrician</h1>
        <p className="mt-1 text-[14px] leading-snug opacity-75">
          The last {REPORT_DAYS} full days of {reportContents(tracking)}
          {list.length > 1 ? ", each baby starting a new page" : ""}. Print it for the checkup, or
          choose Save as PDF in the print dialog to send it ahead.
        </p>
        <div className="mt-4 h-px bg-ink opacity-90" />
        {ready && history.isRefetchError ? (
          <p className="mt-3 text-[13px] text-accent-2">
            Couldn't refresh the older days — showing them as of{" "}
            {clock(new Date(history.dataUpdatedAt))}. The last two days are live.
          </p>
        ) : null}
      </div>

      {ready ? (
        reports.map(({ baby, report }, i) => (
          <BabyReport
            key={baby.id}
            name={baby.name}
            birthDate={baby.birthDate}
            dueDate={baby.dueDate}
            weightOf={(n) => ({
              unit: weightUnit,
              percentile: percentileWords(growth, baby, n.weightGrams, n.at),
            })}
            report={report}
            period={period}
            now={now}
            tracking={tracking}
            unit={unit}
            className={cn("mt-10 print:mt-0", i > 0 && "break-before-page")}
          />
        ))
      ) : history.isError ? (
        <p className="mt-8 text-[14px] text-accent-2">
          Couldn't load the last week — {history.error.message}. Try again in a moment.
        </p>
      ) : (
        <div className="mt-16 flex justify-center" aria-busy="true">
          <Glyph pulse />
        </div>
      )}
    </div>
  );
}
